import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';

import { type MenuConfigItem } from '@/lib/menu/menuConfig';
import { useMenuTree } from '@/lib/menu/useMenuTree';

/**
 * 지금 <b>어느 묶음</b>에 서 있고, 그 묶음의 형제가 누구인가 (사용자 지시 2026-09-22).
 *
 * <h2>사이드바의 나무를 화면 위 탭으로 눕힌다</h2>
 * 예전에는 하위 항목이 사이드바 안에서 펼쳐졌다. 그 모양에는 값이 둘 있었다 — 접은 화살표 칸이
 * 줄마다 자리를 먹고, 하위를 보려면 <b>지금 보는 화면과 상관없이</b> 왼쪽을 한 번 더 눌러야 했다.
 *
 * <p>이제 사이드바는 맨 윗줄만 세우고, 그 아래 것들은 <b>화면 맨 위의 탭</b>으로 선다. 게시판을
 * 누르면 게시판으로 가고, 그 안의 넷은 위에 나란히 놓여 눈으로 고른다. 비워진 화살표 자리에는
 * 별({@code FavoriteStar})이 들어간다 — 요청의 나머지 절반이다.
 *
 * <h2>두 줄까지다</h2>
 * 메뉴는 세 단까지 있다(주간업무 › 본사 · 인사관리 › 휴무 관리 › 현황). 탭도 두 줄까지 세운다 —
 * 첫 줄은 맨 윗줄의 자식, 둘째 줄은 <b>지금 고른 첫 줄 항목의 자식</b>이다. 세 줄이 되면 그것은
 * 탭이 아니라 또 하나의 나무이고, 본문이 화면 아래로 밀린다.
 *
 * @param top     지금 서 있는 맨 윗줄 항목. 홈처럼 메뉴 밖의 화면이면 {@code null}
 * @param tabs    그 아래 첫 줄. 하나뿐이면 탭을 세울 이유가 없어 빈 목록이다
 * @param subTabs 고른 첫 줄 항목의 자식. 없으면 빈 목록
 */
export type MenuSection = {
  top: MenuConfigItem | null;
  tabs: MenuConfigItem[];
  activeTab: MenuConfigItem | null;
  subTabs: MenuConfigItem[];
  activeSubTab: MenuConfigItem | null;
};

export function useMenuSection(): MenuSection {
  const { pathname } = useLocation();
  const { topLevel, childrenOf, isActive } = useMenuTree();

  return useMemo(() => {
    const top = topLevel.find((item) => isActive(item, pathname)) ?? null;
    if (!top) {
      return { top: null, tabs: [], activeTab: null, subTabs: [], activeSubTab: null };
    }

    const children = childrenOf(top.menuKey);
    /*
      묶음 자신의 주소에 서 있으면 <b>첫 탭</b>이 켜진다 (사용자 지적 2026-09-22 「현장현황 눌렀는데
      진행이 기본으로 뜨는데 진행이 선택 안 된 걸로 나온다」).

      현장 현황(`/sites/status`)은 눌러 들어가면 진행 목록을 보여 주지만, 진행 탭이 가리키는 주소는
      `/sites/status/active` 다 — 주소가 달라 아무 탭도 안 켜졌다. 화면에 보이는 것은 진행인데 탭은
      전부 꺼져 있으니, 「내가 어디 있는지」를 탭 줄이 거짓으로 말한 셈이다.

      첫 탭인 이유는 그 화면이 <b>첫 탭을 기본으로 그리기</b> 때문이다(주간업무·보미기술집도 같다).
      순서는 이미 소속에 따라 뒤집혀 있으므로(현장 사람은 현장이 앞) 그 사람에게도 맞는다.
    */
    const landed = top.href != null && pathname === top.href;
    const activeTab = children.find((child) => isActive(child, pathname))
      ?? (landed ? children[0] ?? null : null);
    const grandChildren = activeTab ? childrenOf(activeTab.menuKey) : [];
    /*
      둘째 줄도 <b>부모 주소에 서 있으면 첫 칸</b>이 켜진다(2026-09-30 · 위 `landed` 와 같은 이유).

      현장 현황 › 입찰이 그 자리다. 부모 주소(`/sites/status/bids`)와 첫 자식의 주소
      (`/sites/status/bids/tender`)가 <b>같은 화면</b>인데 다르다 — 한 주소를 두 메뉴가 가리키면
      활성 표시가 둘 다 켜지기 때문이다(`menuRoutes.test`). 그래서 부모 주소로 들어오면 보이는 것은
      첫 자식의 화면인데 둘째 줄은 전부 꺼져 있었다.
    */
    const landedOnTab = activeTab?.href != null && pathname === activeTab.href;
    const activeSubTab = grandChildren.find((child) => isActive(child, pathname))
      ?? (landedOnTab ? grandChildren[0] ?? null : null);

    /*
      자식이 하나뿐이면 탭을 세우지 않는다. 고를 것이 없는 탭 줄은 자리만 먹고, 사람은 그것을
      «눌러도 아무 일 없는 줄» 로 배운다.
    */
    return {
      top,
      tabs: children.length > 1 ? children : [],
      activeTab,
      subTabs: grandChildren.length > 1 ? grandChildren : [],
      activeSubTab,
    };
  }, [pathname, topLevel, childrenOf, isActive]);
}

/**
 * 그 항목을 눌렀을 때 갈 곳 — <b>자기 주소, 없으면 첫 자식</b>.
 *
 * <p>그룹은 주소가 없다(설정·인사관리). 예전에는 눌러서 펼치기만 했으므로 그래도 됐는데, 이제
 * 사이드바에서 누르면 <b>가야 한다</b>. 갈 곳이 없으면 그 줄은 눌리지 않는 줄이 되고, 그것은
 * 「메뉴가 고장 났다」 로 읽힌다.
 *
 * <p>첫 자식도 그룹일 수 있다(인사관리 › 휴무 관리). 주소를 찾을 때까지 내려간다.
 */
export function landingHrefOf(
  item: MenuConfigItem,
  childrenOf: (menuKey: string) => MenuConfigItem[],
): string | null {
  if (item.href) return item.href;
  for (const child of childrenOf(item.menuKey)) {
    const href = landingHrefOf(child, childrenOf);
    if (href) return href;
  }
  return null;
}
